import { useEffect, useState } from "react";
import { useTheme } from "styled-components";

import i18next from "i18next";
import _ from "lodash";
import { useSelector } from "react-redux";

import {
  AgreementBookLink,
  AgreementText,
  Button,
  CheckboxBlock,
  PassengersBlock,
  SubmitBlock,
  Wrapper,
} from "./style";
import { ALL_DOCUMENTS_ITEMS, FRIENDLY_COUNTRY, NP, NP_ID, NP_SR, ONLY_RU_COUNTRY, PS, SR } from "../../../constants";
import { getDomesticDocsCountry } from "../../../utils";
import { useFormik } from "formik";
import { getBookingFormValidationSchema } from "../../../schemas";
import BookingPayerInfoComponent from "../BookingPayerInfoComponent";
import { Header, Title } from "../BookingPayerInfoComponent/style";
import BookingPassengerItemComponent from "../BookingPassengerItemComponent";
import { CheckboxComponent, DotsLoadingComponent } from "../../ui";
import { useToggle } from "../../../hooks";
import { useAuth } from "../../../context/auth";
import { RulesModalComponent } from "../../rulesModal";

const BookingFormComponent = ({ passengers, handleBookClick, isBookingLoading }) => {
  const { t } = i18next;
  const theme = useTheme();
  const { routes, flightType, included, fareRules } = useSelector(state => state.prebook);

  const {
    isTrue: isShowRulesModal,
    set: { on: onOpenRulesModal, off: onCloseRulesModal },
  } = useToggle();

  const { isAuth, user } = useAuth();

  const [agreeWithRules, setAgreeWithRules] = useState(true);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [collapsedPassengers, setCollapsedPassengers] = useState([]); // All passengers expanded by default

  const togglePassenger = (index) => {
    setCollapsedPassengers(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const lastRoute = _.last(routes);
  const deadlineDate = _.last(lastRoute.segments).arrival.time;

  const getPassengers = () => {
    let passengersArray = [];
    let chdArray = [];
    let insArray = [];

    _.forEach(passengers, (count, passengerType) => {
      if (passengerType === "adt") {
        _.times(count, () => {
          passengersArray.push([passengerType]);
        });
      }

      if (passengerType === "chd") {
        _.times(count, () => {
          chdArray.push(passengerType);
        });
      }

      if (passengerType === "ins") {
        _.times(count, () => {
          insArray.push(passengerType);
        });
      }

      if (passengerType === "inf") {
        _.times(count, index => {
          passengersArray[index] = [...passengersArray[index], passengerType];
        });
      }
    });

    const passengersList = [..._.flatten(passengersArray), ...chdArray, ...insArray];

    return passengersList.map((value, index) => {
      let documentItems = [];

      if (flightType === ONLY_RU_COUNTRY) {
        documentItems = value === "adt" ? ALL_DOCUMENTS_ITEMS : ALL_DOCUMENTS_ITEMS.filter(item => item !== PS);
      } else if (getDomesticDocsCountry(flightType)) {
        // Внутренние рейсы по Узбекистану: ID card, детям ещё и свидетельство о рождении
        documentItems = value === "adt" ? [NP_ID, NP] : [NP_SR, NP_ID, NP];
      } else if (flightType === FRIENDLY_COUNTRY) {
        documentItems =
          value === "adt"
            ? ALL_DOCUMENTS_ITEMS.filter(item => item !== SR)
            : ALL_DOCUMENTS_ITEMS.filter(item => item !== PS && item !== SR);
      } else {
        documentItems = ALL_DOCUMENTS_ITEMS.filter(item => item !== PS && item !== SR);
      }

      return {
        type: value,
        surname: "",
        name: "",
        middle_name: "",
        citizenship: "",
        gender: "",
        document_items: documentItems,
        document_type: _.first(documentItems),
        document_number: "",
        expiration_date: "",
        date_of_birth: "",
      };
    });
  };

  const handleSubmitFunction = values => {
    if (!isBookingLoading) {
      handleBookClick(values);
    }
  };

  const form = useFormik({
    initialValues: {
      payer_email: isAuth ? user?.login : "",
      payer_phone: "",
      passengers: getPassengers(),
    },
    validationSchema: getBookingFormValidationSchema(t, deadlineDate),
    onSubmit: handleSubmitFunction,
  });

  // Свёрнутый пассажир с ошибками иначе прячет их: кнопка бы не работала без видимой причины
  const handleSubmitClick = async () => {
    const validationErrors = await form.validateForm();

    const invalidPassengers = _.reduce(
      validationErrors.passengers,
      (acc, passengerErrors, passengerIndex) => (passengerErrors ? [...acc, passengerIndex] : acc),
      []
    );

    if (invalidPassengers.length > 0) {
      setCollapsedPassengers(prev => prev.filter(index => !invalidPassengers.includes(index)));

      document
        .getElementById(`booking-passenger-${_.first(invalidPassengers)}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    form.handleSubmit();

    // FE-26: после отправки Formik проставляет touched — поля получают aria-invalid.
    // Ждём перерисовку и уводим фокус в первое ошибочное поле, иначе тем, кто работает
    // с клавиатуры, непонятно, куда смотреть. Скроллом управляет scrollIntoView выше.
    requestAnimationFrame(() => {
      document.querySelector('[aria-invalid="true"]')?.focus({ preventScroll: true });
    });
  };

  // Check for saved draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('booking_draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        const draftTime = new Date(draft.timestamp);
        const now = new Date();
        const hoursDiff = (now - draftTime) / (1000 * 60 * 60);
        
        // Show draft if it's less than 24 hours old
        if (hoursDiff < 24) {
          setShowDraftBanner(true);
        } else {
          localStorage.removeItem('booking_draft');
        }
      } catch (e) {
        localStorage.removeItem('booking_draft');
      }
    }
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (form.values.payer_email || form.values.payer_phone) {
        const draft = {
          ...form.values,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('booking_draft', JSON.stringify(draft));
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [form.values]);

  const restoreDraft = () => {
    const savedDraft = localStorage.getItem('booking_draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        form.setValues({
          payer_email: draft.payer_email || "",
          payer_phone: draft.payer_phone || "",
          passengers: draft.passengers || form.values.passengers,
        });
        setShowDraftBanner(false);
      } catch (e) {
        console.error('Failed to restore draft:', e);
      }
    }
  };

  const dismissDraft = () => {
    localStorage.removeItem('booking_draft');
    setShowDraftBanner(false);
  };

  return (
    <Wrapper>
      {showDraftBanner && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, minWidth: '200px', fontSize: '14px' }}>
            📝 {t("found_unsaved_booking")}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={restoreDraft}
              style={{
                padding: '8px 16px',
                backgroundColor: '#0052ff',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              {t("restore")}
            </button>
            <button
              onClick={dismissDraft}
              style={{
                padding: '8px 16px',
                backgroundColor: '#fff',
                color: '#666',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              {t("dismiss")}
            </button>
          </div>
        </div>
      )}
      <BookingPayerInfoComponent form={form} />

      <PassengersBlock>
        <Header>
          <Title>{form.values.passengers.length === 1 ? t("passenger_data") : t("passengers")}</Title>
        </Header>
        {form.values.passengers.map((value, index) => {
          const isExpanded = !collapsedPassengers.includes(index) || form.values.passengers.length === 1;
          const hasData = value.name || value.surname || value.document_number;
          
          return (
            <div key={index} id={`booking-passenger-${index}`}>
              {form.values.passengers.length > 1 && (
                <div
                  onClick={() => togglePassenger(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: isExpanded ? '#f0f7ff' : '#f8f9fa',
                    border: `1px solid ${isExpanded ? '#d1e7ff' : '#e0e0e0'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    marginBottom: isExpanded ? '10px' : '0',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '18px' }}>
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>
                      {t("passenger")} {index + 1}
                    </span>
                    {hasData && (
                      <span style={{ color: '#4caf50', fontSize: '16px' }}>✓</span>
                    )}
                  </div>
                  {hasData && !isExpanded && (
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      {value.surname} {value.name}
                    </span>
                  )}
                </div>
              )}
              {/* форму не размонтируем: иначе поля документа и выбранное гражданство сбрасываются */}
              <div style={{ display: isExpanded ? 'block' : 'none' }}>
                <BookingPassengerItemComponent
                  passenger={value}
                  index={index}
                  form={form}
                  flightType={flightType}
                />
              </div>
            </div>
          );
        })}
      </PassengersBlock>

      <CheckboxBlock>
        <div>
          <CheckboxComponent value={agreeWithRules} onChange={() => setAgreeWithRules(prev => !prev)} />
        </div>

        <AgreementText>
          {t("i_read_fares")} <AgreementBookLink onClick={onOpenRulesModal}>{t("agreement_book_link")}</AgreementBookLink>{" "}
          {t("i_accept_fares")}
        </AgreementText>
      </CheckboxBlock>

      <SubmitBlock>
        <div>
          <Button 
            id="booking-form-submit"
            onClick={handleSubmitClick} 
            disabled={!agreeWithRules || isBookingLoading}
          >
            {isBookingLoading ? (
              <>
                <DotsLoadingComponent color={theme.palette.white} /> {t("processing")}...
              </>
            ) : (
              t("book_and_proceed_to_payment")
            )}
          </Button>
        </div>
      </SubmitBlock>

      <RulesModalComponent
        fareRules={fareRules}
        included={included}
        routes={routes}
        actualSegment={null}
        openModal={isShowRulesModal}
        handleCloseModal={onCloseRulesModal}
      />
    </Wrapper>
  );
};

export default BookingFormComponent;